import { getMentorshipPlanMentorTopics } from "api/mentorship-curriculm";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "state/hooks";
import {
  MentorshipPlanMentorTopic,
  MentorshipPlanTemplate,
} from "types/mentorship-plan";
import { MentorshipPlanTopicTransformed } from "../mentorship-curriculum/types/mentorship-plan";

type UseCurriculumVariantBProps = {
  mentorshipPlanDataCurriculam?: MentorshipPlanTemplate;
};

const useCurriculumVariantB = ({
  mentorshipPlanDataCurriculam,
}: UseCurriculumVariantBProps) => {
  const menteeData = useAppSelector((state) => state.planner.menteeData);

  const curriculumStats = useMemo(() => {
    return mentorshipPlanDataCurriculam?.months?.reduce(
      (acc, item) => {
        acc.sessions += item.mentorPlanSessions.length;
        acc.subTopics += item.mentorPlanTopics.length;
        acc.studyMaterials +=
          item.mentorPlanAssignments.length +
          item.mentorPlanProjects.length +
          item.mentorPlanResources.length;
        return acc;
      },
      { sessions: 0, subTopics: 0, studyMaterials: 0 } as {
        sessions: number;
        subTopics: number;
        studyMaterials: number;
      }
    );
  }, [mentorshipPlanDataCurriculam]);

  const topicIdsByMonth = mentorshipPlanDataCurriculam?.months?.map((item) => {
    return {
      month: item.month,
      topicIds: item.mentorPlanTopics,
    };
  });

  const getTopicIdsByMonth = (month: number) => {
    return (
      topicIdsByMonth
        ?.find((item) => item.month === month)
        ?.topicIds.join(",") || ""
    );
  };

  // Fetching mentor topics by month

  const [mentorTopicsByMonth, setMentorTopicsByMonth] = useState<
    (MentorshipPlanMentorTopic & { month: number })[]
  >([]);

  useEffect(() => {
    const fetchTopics = async () => {
      const results = await Promise.all([
        getMentorshipPlanMentorTopics({ topic_ids: getTopicIdsByMonth(1) }),
        getMentorshipPlanMentorTopics({ topic_ids: getTopicIdsByMonth(2) }),
        getMentorshipPlanMentorTopics({ topic_ids: getTopicIdsByMonth(3) }),
        getMentorshipPlanMentorTopics({ topic_ids: getTopicIdsByMonth(4) }),
        getMentorshipPlanMentorTopics({ topic_ids: getTopicIdsByMonth(5) }),
        getMentorshipPlanMentorTopics({ topic_ids: getTopicIdsByMonth(6) }),
      ]);
      const combinedTopics = results.flatMap((topics, idx) =>
        (topics || []).map((item) => ({ ...item, month: idx + 1 }))
      );

      setMentorTopicsByMonth(combinedTopics);
    };

    fetchTopics();
  }, [mentorshipPlanDataCurriculam]);

  const validateMentorPlanAssignments = (assignmentId: string) => {
    return mentorshipPlanDataCurriculam?.months?.filter((month) =>
      month.mentorPlanAssignments.includes(assignmentId)
    );
  };

  const validateMentorPlanProjects = (projectId: string) => {
    return mentorshipPlanDataCurriculam?.months?.filter((month) =>
      month.mentorPlanProjects.includes(projectId)
    );
  };

  const validateMentorPlanResources = (resourceId: string) => {
    return mentorshipPlanDataCurriculam?.months?.filter((month) =>
      month.mentorPlanResources.includes(resourceId)
    );
  };

  const getMentorPlanAssignmentsLength = (assignmentIds: string[]) => {
    return assignmentIds.reduce((acc, assignmentId) => {
      return acc + (validateMentorPlanAssignments(assignmentId)?.length || 0);
    }, 0);
  };

  const getMentorPlanProjectsLength = (projectIds: string[]) => {
    return projectIds.reduce((acc, projectId) => {
      return acc + (validateMentorPlanProjects(projectId)?.length || 0);
    }, 0);
  };

  const getMentorPlanResourcesLength = (resourceIds: string[]) => {
    return resourceIds.reduce((acc, resourceId) => {
      return acc + (validateMentorPlanResources(resourceId)?.length || 0);
    }, 0);
  };

  const transformTopics = useMemo(() => {
    return mentorTopicsByMonth
      .map((topic) => ({
        ...topic,
        topicAssignmentsLength: getMentorPlanAssignmentsLength(
          topic.mentorPlanAssignments
        ),
        topicProjectsLength: getMentorPlanProjectsLength(
          topic.mentorPlanProjects
        ),
        topicResourcesLength: getMentorPlanResourcesLength(
          topic.mentorPlanResources
        ),
      }))
      .sort((a, b) => a.month - b.month);
  }, [mentorTopicsByMonth]);

  const groupMentorTopicsBySubject = useMemo(() => {
    return transformTopics.reduce((acc, item) => {
      acc[item.subject] = [...(acc[item.subject] || []), item];
      return acc;
    }, {} as Record<string, MentorshipPlanTopicTransformed[]>);
  }, [transformTopics]);

  const groupedTopicsLength = useMemo(() => {
    return Object.keys(groupMentorTopicsBySubject).length;
  }, [groupMentorTopicsBySubject]);

  const defaultAnalyticsPayload = {
    candidate: menteeData?.email
      ? {
          email: menteeData?.email,
          name: menteeData?.candidateName,
          // phoneNumber: menteeData?.,
        }
      : "Anonymous",
    curriculumData: {
      templateId: mentorshipPlanDataCurriculam?.recordId,
      title: mentorshipPlanDataCurriculam?.title,
      planDuration: mentorshipPlanDataCurriculam?.templateForMonth,
      totalSessions: curriculumStats?.sessions,
      totalSubTopics: curriculumStats?.subTopics,
      totalStudyMaterials: curriculumStats?.studyMaterials,
    },
  };

  return {
    curriculumStats,
    mentorTopicsByMonth,
    groupMentorTopicsBySubject,
    groupedTopicsLength,
    defaultAnalyticsPayload,
  };
};

export default useCurriculumVariantB;
